import classnames from 'classnames';
import { useCallback, useMemo, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useDispatch } from 'react-redux';
import { Checkbox } from 'ui-components/components/checkbox';
import { FlexColumn } from 'ui-components/components/flex/column';
import { Gap } from 'ui-components/components/flex/gap';
import { FlexRow } from 'ui-components/components/flex/row';
import { Text } from 'ui-components/components/text/text';
import { TextType } from 'ui-components/components/text/text-type';

import { HtmlEditorDrawer } from '../../../components/html-editor/html-editor-drawer';
import { editTemplate, saveTemplate } from '../../../components/html-editor/html-template.api';
import { OrderNumberSearch } from '../../../components/html-editor/source-id-search/order-number-search';
import { Layout, TemplateType } from '../../../components/html-editor/types';

import type { Layout as LayoutType, Orientation as OrientationType, HtmlTemplate } from '../../../components/html-editor/types';
import type { SelectedOrder } from '../../../components/html-editor/source-id-search/types';
import { EMPTY_HTML_TEMPLATES, useGetHtmlTemplates } from '../../../components/html-editor/use-html-templates';
import { ButtonWithLockIcon } from '../../../feature-gates/button-with-lock-icon';
import { useModal } from '../../../hooks/use-modal';
import { usePickListDefaultTemplateKey } from '../../../hooks/use-seller-setting';
import { LogService } from '../../../services/log-service';
import { saveNewSellerSettings } from '../../../store/seller/seller.actions';
import { getSettingsRaw } from '../../../store/seller/seller.api';
import { SettingsPageTitle } from '../components/settings-page-title/settings-page-title';
import sharedStyles from '../settings-page.module.less';
import { DeletePickListTemplate } from './components/delete-pick-list-template/delete-pick-list-template';
import { PickListGrid } from './components/pick-list-grid/pick-list-grid';
import { messages } from './messages';
import styles from './styles.module.less';

import type { PickListGridItem } from './components/pick-list-grid/pick-list-grid';

const PICK_LIST_ALLOWED_LAYOUTS = [Layout.EightAndHalfByEleven];
const PICK_LIST_PREVIEW_ORDER_KEY = 'shipstation_pick_list_preview_order';

const isHtmlTemplate = (t: HtmlTemplate): boolean => !!t.name && !!t.html;

const mapTemplatesToGridItems = (templates: HtmlTemplate[], isShared: boolean): PickListGridItem[] =>
  templates.filter(isHtmlTemplate).map((t) => ({ ...t, shared: isShared }));

export const PickListHtmlTemplatePage = () => {
  const intl = useIntl();
  const dispatch = useDispatch();
  const defaultTemplateKey = usePickListDefaultTemplateKey();
  const { open: openHtmlEditor, close: closeHtmlEditor, isOpen: isHtmlEditorOpen } = useModal();
  const {
    data: htmlTemplatesResponse = EMPTY_HTML_TEMPLATES,
    refetch: refetchHtmlTemplates,
    isLoading,
  } = useGetHtmlTemplates(TemplateType.PickList);

  const [editingTemplateId, setEditingTemplateId] = useState<string | undefined>(undefined);
  const [isCopy, setIsCopy] = useState(false);
  const [isDefault, setIsDefault] = useState(false);
  const [deletingTemplate, setDeletingTemplate] = useState<PickListGridItem | null>(null);
  const [selectedOrder, setSelectedOrder] = useState<SelectedOrder | null>(() => {
    try {
      const stored = sessionStorage.getItem(PICK_LIST_PREVIEW_ORDER_KEY);
      return stored ? JSON.parse(stored) : null;
    } catch {
      return null;
    }
  });

  const allTemplates = useMemo(() => {
    const shared = mapTemplatesToGridItems(htmlTemplatesResponse.shared, true);
    const customer = mapTemplatesToGridItems(htmlTemplatesResponse.customer, false);
    return [...shared, ...customer];
  }, [htmlTemplatesResponse.shared, htmlTemplatesResponse.customer]);

  const selectedTemplate = useMemo(() => {
    if (!editingTemplateId) return undefined;
    return allTemplates.find((t) => t.id === editingTemplateId);
  }, [editingTemplateId, allTemplates]);

  const sharedDefaultTemplate = useMemo(() => {
    return allTemplates.find((t) => t.shared && t.layout === Layout.EightAndHalfByEleven);
  }, [allTemplates]);

  const effectiveDefaultTemplateId = defaultTemplateKey || sharedDefaultTemplate?.id;

  const initialIsDefault = useMemo(() => {
    if (isCopy || !editingTemplateId) return false;
    return editingTemplateId === defaultTemplateKey;
  }, [isCopy, editingTemplateId, defaultTemplateKey]);

  const handleNewTemplate = useCallback(() => {
    setEditingTemplateId(undefined);
    setIsCopy(false);
    setIsDefault(false);
    openHtmlEditor();
  }, [openHtmlEditor]);

  const handleEditTemplate = useCallback(
    (id: string) => {
      setEditingTemplateId(id);
      setIsCopy(false);
      setIsDefault(id === defaultTemplateKey);
      openHtmlEditor();
    },
    [openHtmlEditor, defaultTemplateKey],
  );

  const handleCopyTemplate = useCallback(
    (id: string) => {
      setEditingTemplateId(id);
      setIsCopy(true);
      setIsDefault(false);
      openHtmlEditor();
    },
    [openHtmlEditor],
  );

  const handleDeleteTemplate = useCallback(
    (id: string) => {
      const template = allTemplates.find((t) => t.id === id);
      if (template) {
        setDeletingTemplate(template);
      }
    },
    [allTemplates],
  );

  const handleCloseHtmlEditor = useCallback(() => {
    closeHtmlEditor();
    setEditingTemplateId(undefined);
    setIsCopy(false);
    setIsDefault(false);
  }, [closeHtmlEditor]);

  const handleSaveHtmlTemplate = useCallback(
    (data: { name: string; html: string; layout: LayoutType; orientation: OrientationType }) => {
      const hasDefaultChanged = isDefault !== initialIsDefault;
      const request = hasDefaultChanged ? { ...data, settings: { isDefault } } : data;

      if (!isCopy && selectedTemplate) {
        return editTemplate(TemplateType.PickList, selectedTemplate.id, request);
      }
      return saveTemplate(TemplateType.PickList, request);
    },
    [isCopy, selectedTemplate, isDefault, initialIsDefault],
  );

  const handleHtmlTemplateSaved = useCallback(() => {
    const hasDefaultChanged = isDefault !== initialIsDefault;
    refetchHtmlTemplates();
    if (hasDefaultChanged) {
      getSettingsRaw()
        .then((response) => dispatch(saveNewSellerSettings(response.data)))
        .catch((error) =>
          LogService.logError(new Error('Failed to refresh seller settings after pick list default change', { cause: error })),
        );
    }
    setEditingTemplateId(undefined);
    setIsCopy(false);
    setIsDefault(false);
  }, [refetchHtmlTemplates, isDefault, initialIsDefault, dispatch]);

  const handleCancelDelete = useCallback(() => {
    setDeletingTemplate(null);
  }, []);

  const handleDeleted = useCallback(() => {
    if (deletingTemplate?.id === defaultTemplateKey) {
      getSettingsRaw()
        .then((response) => dispatch(saveNewSellerSettings(response.data)))
        .catch((error) =>
          LogService.logError(new Error('Failed to refresh seller settings after pick list default deletion', { cause: error })),
        );
    }
    setDeletingTemplate(null);
  }, [deletingTemplate, defaultTemplateKey, dispatch]);

  const sourceIds = selectedOrder?.sourceIds ?? null;

  const handleOrderSelected = useCallback((order: SelectedOrder) => {
    setSelectedOrder(order);
    sessionStorage.setItem(PICK_LIST_PREVIEW_ORDER_KEY, JSON.stringify(order));
  }, []);

  const handleOrderCleared = useCallback(() => {
    setSelectedOrder(null);
    sessionStorage.removeItem(PICK_LIST_PREVIEW_ORDER_KEY);
  }, []);

  const orderSearchToolbar = useMemo(
    () => (
      <OrderNumberSearch
        isDisabled={false}
        onClear={handleOrderCleared}
        onOrderSelected={handleOrderSelected}
        selectedOrder={selectedOrder}
      />
    ),
    [selectedOrder, handleOrderCleared, handleOrderSelected],
  );

  const additionalFormFields = useMemo(
    () => (
      <FlexColumn className={styles['additional-form-field']} gap={Gap.Space4}>
        <Text textType={TextType.Label} value={messages.defaultTemplateSectionLabel} />
        <Checkbox
          checked={isDefault}
          className={styles['default-checkbox']}
          label={intl.formatMessage(messages.setAsDefault)}
          largeCheckbox
          onChange={setIsDefault}
        />
      </FlexColumn>
    ),
    [isDefault, intl],
  );

  return (
    <div className={classnames(sharedStyles.content, styles['template-content'])}>
      <SettingsPageTitle value={messages.header} />
      <Text className={styles.summary} textType={TextType.Description} value={messages.pageSummary} />
      <div>
        <FlexRow className={styles['grid-header-row']}>
          <Text textType={TextType.H3} value={messages.allPickLists} />
          <ButtonWithLockIcon onClick={handleNewTemplate}>
            <FormattedMessage {...messages.newTemplate} />
          </ButtonWithLockIcon>
        </FlexRow>
        <PickListGrid
          defaultTemplateKey={effectiveDefaultTemplateId}
          isLoading={isLoading}
          items={allTemplates}
          onCopyTemplate={handleCopyTemplate}
          onDeleteTemplate={handleDeleteTemplate}
          onEditTemplate={handleEditTemplate}
        />
        {isHtmlEditorOpen ? (
          <HtmlEditorDrawer
            additionalFormFields={additionalFormFields}
            allowedLayouts={PICK_LIST_ALLOWED_LAYOUTS}
            initialLayout={selectedTemplate?.layout ?? sharedDefaultTemplate?.layout ?? Layout.EightAndHalfByEleven}
            initialOrientation={selectedTemplate?.orientation ?? sharedDefaultTemplate?.orientation}
            initialTemplateName={
              isCopy && selectedTemplate?.name
                ? intl.formatMessage(messages.copySuffix, { name: selectedTemplate.name })
                : selectedTemplate?.name
            }
            initialValue={selectedTemplate?.html ?? sharedDefaultTemplate?.html}
            onCancel={handleCloseHtmlEditor}
            onSave={handleSaveHtmlTemplate}
            onSuccessfulSave={handleHtmlTemplateSaved}
            previewToolbar={orderSearchToolbar}
            sourceIds={sourceIds}
            templateType={TemplateType.PickList}
            title={<Text textType={TextType.H3} value={messages.htmlEditorTitle} />}
          />
        ) : null}
        {deletingTemplate ? (
          <DeletePickListTemplate
            onCancel={handleCancelDelete}
            onDeleted={handleDeleted}
            templateId={deletingTemplate.id}
            templateName={deletingTemplate.name}
          />
        ) : null}
      </div>
    </div>
  );
};
